import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
// Route components are code-split: each page becomes its own JS chunk so the
// home route doesn't pay for the entire app's bundle. ~459 KiB of JS on the
// home page was unused before this split (Lighthouse mobile, 2026-04-30).
const SearchPage = lazy(() => import("./components/pages/SearchPage"));
const AuthPage = lazy(() => import("./components/pages/AuthPage"));
const DashboardPage = lazy(() => import("./components/pages/DashboardPage"));
const SavedListPage = lazy(() => import("./components/pages/SavedListPage"));
const OAuthCallbackPage = lazy(() => import("./components/pages/OAuthCallbackPage"));
const AdminPage = lazy(() => import("./components/pages/AdminPage"));
const EnrichmentPage = lazy(() => import("./components/pages/EnrichmentPage"));
const ApartmentBuilderPage = lazy(() => import("./components/pages/ApartmentBuilderPage"));
const BrowseMapPage = lazy(() => import("./components/pages/BrowseMapPage"));
const VibeSearchPage = lazy(() => import("./components/pages/VibeSearchPage"));
const NeighborhoodExplorerPage = lazy(() => import("./components/pages/NeighborhoodExplorerPage"));
const NeighborhoodReportRoute = lazy(() => import("./components/neighborhood-report/NeighborhoodReportPage"));
const BuildingPage = lazy(() => import("./components/building-page/BuildingPage"));
const ComparisonPage = lazy(() => import("./components/comparison-page/ComparisonPage"));
const ComparisonHubPage = lazy(() => import("./components/comparison-page/ComparisonHubPage"));
const BuildingComparisonPage = lazy(() => import("./components/comparison-page/BuildingComparisonPage"));
const QuestionLibraryPage = lazy(() => import("./components/question-library/QuestionLibraryPage"));
const QuestionPage = lazy(() => import("./components/question-library/QuestionPage"));
const NeighborhoodMatchPage = lazy(() => import("./components/neighborhood-match/NeighborhoodMatchPage"));
const AskTacoPage = lazy(() => import("./components/ask-taco/AskTacoPage"));
const AdminUserDetailPage = lazy(() => import("./components/pages/AdminUserDetailPage"));
const NotFoundPage = lazy(() => import("./components/pages/NotFoundPage"));
const PriceReportPage = lazy(() => import("./components/pages/PriceReportPage"));
const VerifyEmailPage = lazy(() => import("./components/pages/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("./components/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./components/pages/ResetPasswordPage"));
const SignedOutProfilePage = lazy(() => import("./components/pages/SignedOutProfilePage"));
import HomePage from "./components/pages/HomePage";
const HandoffResumePage = lazy(() => import("./components/pages/HandoffResumePage"));
const IntakePage = lazy(() => import("./components/pages/IntakePage"));
const OkToTextPage = lazy(() => import("./components/intake/OkToTextPage"));
import { TourCartFloat } from "./components/molecules/TourCartWidgets";
import { ThreadReturnChip } from "./components/molecules/ThreadReturnChip";
import { SaveLimitModal } from "./components/molecules/SaveLimitModal";
import { Toaster } from "./components/ui/toaster";
import { NavigationBar } from "./components/organisms/NavigationBar";
import { rememberMetro } from "./stores/metroAccount";
import { useAuthStore } from "./stores/authStore";
import { useUserProfileStore } from "./stores/userProfileStore";
import { AppErrorBoundary } from "./components/ErrorBoundary";
import { useTracking } from "./hooks/useTracking";
import { RouteMetadata } from '@/components/RouteMetadata';
import { AnalyticsConsent } from '@/components/analytics/AnalyticsConsent';

/** /tour-checkout → /price-report, preserving the query string (names=…).
 * The page is Price-Report-only now (ADR-0012); the old route name was the
 * next drift artifact waiting to happen. */
function TourCheckoutRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/price-report", search: location.search }} replace />;
}

/** Preserve the Downtown URL used by the WordPress launch while consolidating
 * all indexable signals and visitors on the editorial report's canonical URL. */
function DowntownGuideRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/n/austin/downtown", search: location.search }} replace />;
}

/** /houston and /san-antonio (and the other launched metros) select that
 *  city and land on the home hero. Direct loads are rewritten in render.yaml. */
function MetroHomeRedirect({ metro }: { metro: string }) {
  useEffect(() => {
    rememberMetro(metro);
  }, [metro]);
  return <Navigate to="/" replace />;
}

/** Fires a `page_view` event on first load and on every route change.
 *
 * This is the floor of the admin funnel. Before it existed, `visited` was
 * defined as "sessions with any event at all" — but no event fired until the
 * renter searched or saved, so the top of the funnel was really "already
 * engaged" and every conversion rate below it was measured against the wrong
 * denominator.
 *
 * Keyed on pathname only: query-string changes (filters, ?names=) are the same
 * page and would otherwise fire a burst of events per interaction. */
function RouteTracker() {
  const { pathname } = useLocation();
  const { track } = useTracking();

  useEffect(() => {
    track('page_view', 'app', { path: pathname });
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

const RouteFallback = () => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ background: 'var(--ts-bg-base)' }}
    aria-busy="true"
  >
    <div
      className="w-10 h-10 rounded-full animate-pulse"
      style={{ background: 'var(--ts-accent-primary)', opacity: 0.4 }}
    />
  </div>
);



/** Redirect to login if not authenticated.
 * Gates on `user`, not `token`: the JWT now lives in an HttpOnly cookie and is
 * null client-side after a reload, so token-gating bounced signed-in users to
 * /login on every refresh. `user` is restored optimistically from localStorage
 * and confirmed/cleared by checkAuth() on app load (see App()). */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, authChecked } = useAuthStore();
  // Cookie-only sessions have no optimistic localStorage `user`; wait for the
  // one-time checkAuth() (fired in App) before deciding, so we don't bounce a
  // valid session to /login on the synchronous first render.
  if (!user && !authChecked) return <RouteFallback />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/** Redirect to home if not admin.
 * Two layers of protection:
 * 1. Frontend: AdminRoute prevents non-admins from seeing admin UI
 * 2. Backend: require_admin dependency rejects API calls from non-admins
 * Both must be in place. Frontend alone is not enough (users can bypass it).
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, authChecked } = useAuthStore();
  if (!user && !authChecked) return <RouteFallback />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user?.is_admin && user?.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function App() {
  // Restore the signed-in session on every load via the HttpOnly cookie.
  // Without this, the in-memory token is gone after a reload and the user
  // looked logged out (nav + protected routes) despite a valid session.
  useEffect(() => {
    useAuthStore.getState().checkAuth();
  }, []);

  useEffect(() => {
    useUserProfileStore.getState().syncFromLocalStorage();
    const handler = () => {
      useUserProfileStore.getState().syncFromLocalStorage();
    };
    window.addEventListener('savedApartmentsChanged', handler);
    window.addEventListener('notesChanged', handler);
    return () => {
      window.removeEventListener('savedApartmentsChanged', handler);
      window.removeEventListener('notesChanged', handler);
    };
  }, []);

  return (
    <AppErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AnalyticsConsent />
          <RouteTracker />
          <RouteMetadata />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/austin" element={<MetroHomeRedirect metro="Austin" />} />
            <Route path="/dallas" element={<MetroHomeRedirect metro="Dallas" />} />
            <Route path="/houston" element={<MetroHomeRedirect metro="Houston" />} />
            <Route path="/san-antonio" element={<MetroHomeRedirect metro="San Antonio" />} />
            {/* Retired entry routes (plan R2-5) — the vibe-first home is the
                gate now. Old links/bookmarks land on the hero. */}
            <Route path="/start" element={<Navigate to="/" replace />} />
            <Route path="/metro" element={<Navigate to="/" replace />} />
            <Route path="/landing" element={<Navigate to="/" replace />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/vibe" element={<VibeSearchPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/oauth-callback" element={<OAuthCallbackPage />} />
            <Route path="/profile" element={<SignedOutProfilePage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route path="/my-list" element={<SavedListPage />} />
            <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/users/:userId" element={<AdminRoute><AdminUserDetailPage /></AdminRoute>} />
            <Route path="/explore" element={<NeighborhoodExplorerPage />} />
            <Route path="/n/austin/downtown-austin" element={<DowntownGuideRedirect />} />
            <Route path="/n/:city/:slug" element={<NeighborhoodReportRoute />} />
            <Route path="/:city/apartments/:slug" element={<BuildingPage />} />
            <Route path="/austin/compare" element={<ComparisonHubPage />} />
            <Route path="/austin/compare/buildings" element={<BuildingComparisonPage />} />
            <Route path="/austin/questions" element={<QuestionLibraryPage />} />
            <Route path="/austin/questions/:slug" element={<QuestionPage />} />
            <Route path="/austin/neighborhood-match" element={<NeighborhoodMatchPage />} />
            <Route path="/austin/ask" element={<AskTacoPage />} />
            <Route path="/:city/compare/:slug" element={<ComparisonPage />} />
            <Route path="/browse" element={<BrowseMapPage />} />
            <Route path="/enrichment" element={<AdminRoute><EnrichmentPage /></AdminRoute>} />
            <Route path="/apartment-builder" element={<AdminRoute><ApartmentBuilderPage /></AdminRoute>} />
            <Route path="/price-report" element={<PriceReportPage />} />
            <Route path="/handoff/:handoffId" element={<HandoffResumePage />} />
            <Route path="/intake/ok-to-text" element={<OkToTextPage />} />
            <Route path="/:city/intake" element={<IntakePage />} />
            <Route path="/tour-checkout" element={<TourCheckoutRedirect />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          <SaveLimitModal />
          {/* Save/unsave feedback toasts (announceSaveToggle in savedListUtils) */}
          <Toaster />
          {/* Persistent price report CTA — appears once renter has saved ≥1 apartment */}
          <TourCartFloat />
          {/* One thread, one search — quiet way back to the conversation */}
          <ThreadReturnChip />
        </BrowserRouter>
      </div>
    </AppErrorBoundary>
  );
}

export default App;
